import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderTableElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.FAQ_ACCORDION:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0,
        ...getSelectionStyles(element.id)
      }}
      onClick={e => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        // For MEDIA elements, show Content tab (0) where upload controls are
        // For other elements, show Style tab (1)
        setTabValue(element.type === ELEMENT_TYPES.MEDIA ? 0 : 1);
        setRightPanelOpen(true);
      }}
      title="Click to select and edit FAQAccordion"
    >
      <ElementControls />
      <FAQAccordion
        title={element.title}
        items={element.items}
        iconType={element.iconType || 'plusMinus'}
        iconColor={element.iconColor || '#36C3D6'}
        iconSize={element.iconSize || 'large'}
        showIcon={element.showIcon !== false}
        textAlign={element.textAlign || 'left'}
        containerBackgroundColor={element.containerBackgroundColor || 'transparent'}
        questionBackgroundColor={element.questionBackgroundColor || 'transparent'}
        answerBackgroundColor={element.answerBackgroundColor || 'rgba(0, 0, 0, 0.02)'}
        questionTextColor={element.questionTextColor || '#4B329A'}
        answerTextColor={element.answerTextColor || '#4B329A'}
        titleTextColor={element.titleTextColor || 'primary'}
        showDivider={element.showDivider !== false}
        dividerColor={element.dividerColor || '#C1A7F1'}
        itemBorderRadius={element.itemBorderRadius || '4px'}
        itemSpacing={element.itemSpacing || '12px'}
        questionPadding={element.questionPadding || '16px'}
        answerPadding={element.answerPadding || '16px'}
      />
    </Box>
  );
case ELEMENT_TYPES.COMPARISON_TABLE:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0,
        ...getSelectionStyles(element.id)
      }}
      onClick={e => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Content tab for editing
        setRightPanelOpen(true);
      }}
      title="Click to select and edit ComparisonTable"
    >
      <ElementControls />
      <ComparisonTable
        key={`${element.id}-${element.productAImage || 'noA'}-${element.productBImage || 'noB'}`}
        title={element.title}
        productA={element.productA}
        productB={element.productB}
        productAImage={element.productAImage}
        productBImage={element.productBImage}
        rows={element.rows}
        titleStylePreset={element.titleStylePreset}
        productNameStylePreset={element.productNameStylePreset}
        featureTitleStylePreset={element.featureTitleStylePreset}
        featureDescStylePreset={element.featureDescStylePreset}
      />

    </Box>
  );
case ELEMENT_TYPES.TWO_COLUMN_COMPARISON:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0,
        ...getSelectionStyles(element.id)
      }}
      onClick={e => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Content tab for editing
        setRightPanelOpen(true);
      }}
      title="Click to select and edit Two Column Comparison"
    >
      <ElementControls />
      <TwoColumnComparisonTable
        key={`${element.id}-${element.leftColumnImage || 'noLeft'}-${element.rightColumnImage || 'noRight'}`}
        leftColumnTitle={element.leftColumnTitle}
        rightColumnTitle={element.rightColumnTitle}
        leftColumnImage={element.leftColumnImage}
        rightColumnImage={element.rightColumnImage}
        leftItems={element.leftItems}
        rightItems={element.rightItems}
        titleStylePreset={element.titleStylePreset}
        itemStylePreset={element.itemStylePreset}
      />
    </Box>
  );
case ELEMENT_TYPES.STAR_RATING:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0
      }}
    >
      <ElementControls />
      {/* Alignment wrapper: positions the inline-flex group left/center/right */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: element.styles?.justifyContent || 'center',
          textAlign: element.styles?.textAlign || 'center',
          marginBottom: element.styles?.marginBottom || '10px',
          cursor: 'pointer',
          '&:hover': {
            outline: '2px dashed #1976d2',
            outlineOffset: '2px'
          },
          ...getSelectionStyles(element.id)
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setSelectedSubElement(null);
          setTabValue(1);
          setRightPanelOpen(true);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          handleDoubleClick('star_rating');
        }}
      >
        {/* Inner group: text + stars as inline-flex so alignment wrapper can center/align it */}
        <Box
          sx={{
            color: element.styles?.color || '#ffc107',
            fontSize: element.styles?.fontSize || '24px',
            ...element.styles,
            // Must come AFTER spread so they override element.styles.gap / display
            display: 'inline-flex',
            alignItems: (element.ratingTextPosition === 'top' || element.ratingTextPosition === 'bottom') ? 'flex-start' : 'center',
            flexDirection: (element.ratingTextPosition === 'top' || element.ratingTextPosition === 'bottom') ? 'column' : 'row',
            gap: element.ratingTextGap !== undefined ? element.ratingTextGap : '8px',
            justifyContent: undefined,
            textAlign: undefined,
            marginBottom: undefined,
          }}
        >
          {element.showRatingText && (!element.ratingTextPosition || element.ratingTextPosition === 'left' || element.ratingTextPosition === 'top') && (
            <Typography component="span" sx={{ fontWeight: 'bold', fontSize: 'inherit', color: 'inherit', lineHeight: 1, ...(element.ratingTextStyles || {}) }}>
              {element.ratingText !== undefined ? element.ratingText : `${element.rating || 5}/${element.maxStars || 5}`}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: element.styles?.gap || '4px' }}>
            {Array.from({ length: element.maxStars || 5 }).map((_, idx) => (
              <Box key={idx} sx={{ color: idx < (element.rating || 5) ? element.styles?.color || '#ffc107' : '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                <StarIcon fontSize="inherit" />
              </Box>
            ))}
          </Box>
          {element.showRatingText && (element.ratingTextPosition === 'right' || element.ratingTextPosition === 'bottom') && (
            <Typography component="span" sx={{ fontWeight: 'bold', fontSize: 'inherit', color: 'inherit', lineHeight: 1, ...(element.ratingTextStyles || {}) }}>
              {element.ratingText !== undefined ? element.ratingText : `${element.rating || 5}/${element.maxStars || 5}`}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
case ELEMENT_TYPES.STAR_RATING_TABLE:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: element.styles?.marginBottom || '20px',
        backgroundColor: element.styles?.backgroundColor || '#ffffff',
        padding: element.styles?.padding || '24px',
        borderRadius: element.styles?.borderRadius || '12px',
        border: element.styles?.border || '1px solid #eeeeee',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setSelectedSubElement(null);
        setTabValue(0); // Content tab for editing rows
        setRightPanelOpen(true);
      }}
    >
      <ElementControls />
      {element.showTitle !== false && element.title && (
        <Typography
          variant="h6"
          sx={{
            color: element.styles?.titleColor || '#000000',
            fontSize: element.styles?.titleFontSize || '20px',
            fontWeight: 'bold',
            mb: 2.5,
            textAlign: 'left',
            lineHeight: 1.2
          }}
        >
          {element.title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: element.styles?.justifyContent || 'flex-start' }}>
        {(element.rows || []).map((row, idx) => {
          const isSideBySide = element.rowLayout === 'side-by-side';
          const labelAlign = element.labelAlign || 'left';
          const starsAlign = element.starsAlign || (isSideBySide ? 'right' : 'left');

          return (
            <Box
              key={row.id || idx}
              sx={{
                py: 1.5,
                width: '100%',
                display: 'flex',
                flexDirection: isSideBySide ? 'row' : 'column',
                alignItems: isSideBySide ? 'center' : 'stretch',
                justifyContent: isSideBySide ? 'space-between' : 'flex-start',
                borderBottom: element.showRowDividers && idx < (element.rows || []).length - 1 ? `${element.dividerThickness || '1px'} solid ${element.dividerColor || '#eeeeee'}` : 'none',
                '&:last-child': { pb: 0 },
                '&:first-of-type': { pt: 0 },
                gap: isSideBySide ? 2 : 0
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: element.styles?.fontWeight || '700',
                  fontFamily: element.styles?.fontFamily || 'inherit',
                  mb: isSideBySide ? 0 : 0.5,
                  color: element.styles?.color || '#1a1a1a',
                  fontSize: element.styles?.labelFontSize || '18px',
                  lineHeight: 1.4,
                  textAlign: labelAlign,
                  flex: isSideBySide ? 1 : 'none',
                  minWidth: isSideBySide ? '100px' : 'auto'
                }}
              >
                {row.label}
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flex: isSideBySide ? 2 : 'none',
                width: '100%',
                justifyContent: isSideBySide ?
                  (starsAlign === 'left' ? 'flex-start' : starsAlign === 'center' ? 'center' : 'flex-end') :
                  (starsAlign === 'right' ? 'flex-end' : starsAlign === 'center' ? 'center' : 'flex-start')
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'inherit',
                  width: '100%',
                  gap: 1.5
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: element.styles?.gap || '4px',
                    flex: starsAlign === 'center' ? 'none' : 1,
                    justifyContent: starsAlign === 'center' ? 'center' : starsAlign === 'right' ? 'flex-end' : 'flex-start',
                    order: element.ratingTextPosition === 'left' ? 2 : 1
                  }}>
                    {Array.from({ length: element.maxStars || 5 }).map((_, i) => {
                      const isFull = i + 1 <= Math.floor(row.rating);
                      const isHalf = !isFull && i < row.rating;
                      return (
                        <StarIcon
                          key={i}
                          sx={{
                            fontSize: element.styles?.fontSize || '22px',
                            color: (isFull || isHalf) ? (element.styles?.starColor || element.styles?.color || '#ffc107') : '#e0e0e0',
                          }}
                        />
                      );
                    })}
                  </Box>

                  {row.ratingText && (
                    <Box sx={{
                      flex: element.ratingTextAlign === 'center' ? 'none' : 1,
                      textAlign: element.ratingTextAlign || 'left',
                      order: element.ratingTextPosition === 'left' ? 1 : 2
                    }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: element.styles?.fontWeight || '800',
                          fontFamily: element.styles?.fontFamily || 'inherit',
                          color: element.styles?.color || '#1a1a1a',
                          fontSize: element.styles?.labelFontSize || '18px'
                        }}
                      >
                        {row.ratingText}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
    default:
      return null;
  }
};
